import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useFormProgress } from '../../../hooks/useFormProgress';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { fetchApplicationDetails, saveFormData } from '../../../utils/apiUtils';
import styled from 'styled-components';
import FilePreviewModal from '../../common/FilePreviewModal';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import FormPageNavigator from '../../common/FormPageNavigator';
import SessionTimer from '../../common/SessionTimer';

// API URL configuration
const API_URL_CONFIG = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL_CONFIG.endsWith('/api') ? API_URL_CONFIG.slice(0, -4) : API_URL_CONFIG;

// Helper function to generate a unique application ID
const generateApplicationId = (): string => {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
  const year = String(now.getFullYear());
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
  const randomChar = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'[Math.floor(Math.random() * 32)];
  return `CNG-${day}${month}${year}${hours}${minutes}${seconds}-${milliseconds}${randomChar}`;
};

// A4 Dimensions (approximate for screen, maintaining aspect ratio)
// 210mm / 297mm = 0.707
const A4_WIDTH_PX = 794; // Approx 210mm at 96 DPI
const A4_HEIGHT_PX = 1123; // Approx 297mm at 96 DPI

// Progress bar at top of page
const ProgressBarContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 6px;
  background-color: #e0e0e0;
  z-index: 1000;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ProgressBarFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${props => `${props.progress}%`};
  background-color: #6cb288;
  transition: width 0.5s ease-in-out;
`;

const ProgressLabel = styled.div`
  position: fixed;
  top: 10px;
  right: 10px;
  background-color: #f8fcfa;
  color: #6cb288;
  border: 1px solid #a8d0b9;
  border-radius: 20px;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 500;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  z-index: 1000;
`;

// Zoom controls
const ZoomControlsContainer = styled.div`
  position: fixed;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 1000;
`;

const ZoomButton = styled.button`
  width: 30px;
  height: 30px;
  background-color: white;
  color: #6cb288;
  border: 1px solid #a8d0b9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
  padding: 0;
  
  &:hover {
    background-color: #f8fcfa;
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const OfflineIndicator = styled.div`
  background-color: #dc3545; /* Red for offline */
  color: white;
  text-align: center;
  padding: 10px;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 2000; /* Ensure it's above other elements */
  font-size: 0.9em;
`;

const PageWrapper = styled.div<{ scale: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  min-height: 100vh;
  background-color: #f5f5f5;
  transform: scale(${props => props.scale});
  transform-origin: center top;
  transition: transform 0.3s ease;
`;

const A4PageContainer = styled.div`
  width: ${A4_WIDTH_PX}px;
  min-height: ${A4_HEIGHT_PX}px; /* Use min-height to allow content to expand if needed */
  background-color: white;
  padding: 50px; /* Increased padding for more breathing room */
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.07); /* Softer, more modern shadow */
  box-sizing: border-box;
  position: relative; /* For page number positioning */
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
  color: #212529; /* Darker, more standard text color */
  font-size: 13px; /* Base font size for content */
  line-height: 1.6;
`;

const FloatingBackButton = styled.button`
  position: fixed;
  bottom: 30px;
  left: 30px;
  background: linear-gradient(145deg, #4a6cf7, #5e54e2); /* Modern gradient */
  color: white;
  border: none;
  border-radius: 50%; /* Circular button */
  width: 60px; /* Size of the button */
  height: 60px;
  font-size: 22px; /* Slightly smaller icon size for a cleaner look */
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(74, 108, 247, 0.3), 0 3px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000; /* Ensure it's on top */
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  overflow: hidden;
  font-weight: bold;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0));
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-5px) scale(1.05);
    box-shadow: 0 10px 20px rgba(74, 108, 247, 0.4), 0 6px 6px rgba(0, 0, 0, 0.1);
    
    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(0) scale(0.98);
    box-shadow: 0 3px 8px rgba(74, 108, 247, 0.2), 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  &::after {
    content: 'Back';
    position: absolute;
    font-size: 12px;
    bottom: 7px;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::after {
    opacity: 1;
  }
`;

const FormContentContainer = styled.div`
  /* Form styles */
`;

const PageNumber = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  font-size: 0.8em;
  color: #777;
`;

// ADD THIS ENTIRE BLOCK
const ProceedButton = styled.button`
  background: linear-gradient(145deg, #28a745, #218838); // Green gradient
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
  transition: all 0.3s ease;
  min-width: 200px; // Ensure it has a decent width

  &:hover {
    background: linear-gradient(145deg, #218838, #1e7e34);
    box-shadow: 0 6px 16px rgba(40, 167, 69, 0.4);
    transform: translateY(-2px);
  }

  &:active {
    background: linear-gradient(145deg, #1e7e34, #1c7430);
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(40, 167, 69, 0.2);
  }

  &:disabled {
    background: #cccccc;
    color: #666666;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }
`;

const SaveIndicator = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 50%;
  left: 20px;
  transform: translateY(-50%);
  background-color: #f8fcfa;
  color: #6cb288;
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid #a8d0b9;
  font-size: 0.8em;
  opacity: ${props => props.visible ? 1 : 0};
  transition: opacity 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  z-index: 10000;
`;

const SaveDraftButton = styled.button`
  background-color: #ffc107; /* Amber color for draft */
  color: #212529; /* Dark text for contrast */
  padding: 10px 25px;
  border: none;
  border-radius: 6px;
  font-size: 1em;
  font-weight: 500;
  letter-spacing: 0.5px;
  cursor: pointer;
  margin-top: 15px; /* Space from submit button or other elements */
  margin-right: 10px; /* Space from submit button if on same line */
  display: inline-block; /* Allow side-by-side with submit if desired */
  transition: background-color 0.15s ease-in-out, transform 0.1s ease-in-out;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  &:hover {
    background-color: #e0a800; /* Darker amber on hover */
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }

  &:active {
    background-color: #c69500;
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  }

  &:disabled {
    background-color: #ccc;
    color: #666;
    cursor: not-allowed;
  }
`;

const PayNowButton = styled.button`
  background-color: #007bff;
  color: white;
  padding: 10px 25px;
  border: none;
  border-radius: 6px; /* Slightly larger radius */
  font-size: 1em; /* Relative to base */
  font-weight: 500;
  letter-spacing: 0.5px;
  cursor: pointer;
  margin-top: 30px; /* More space above */
  display: block; 
  margin-left: auto;
  margin-right: auto;
  transition: background-color 0.15s ease-in-out, transform 0.1s ease-in-out;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  &:hover {
    background-color: #0069d9; /* Darker shade on hover */
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }

  &:active {
    background-color: #0056b3;
    transform: translateY(1px); /* Slight press effect */
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(145deg, #28a745, #218838);
  color: white;
  padding: 12px 30px;
  border: none;
  border-radius: 6px;
  font-size: 1.1em;
  font-weight: 600;
  letter-spacing: 0.5px;
  cursor: pointer;
  display: inline-block;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  
  &:hover {
    background: linear-gradient(145deg, #218838, #1e7e34);
    box-shadow: 0 6px 8px rgba(0,0,0,0.15);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ActionButtonsContainer = styled.div`
  margin-top: 30px;
  width: 100%;
`;

// Form specific styles
const FormTitle = styled.h2`
  text-align: center;
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 8px;
`;

const SubTitle = styled.p`
  text-align: center;
  font-size: 12px;
  margin-bottom: 10px;
  font-style: italic;
  font-weight: bold;
`;

const AffidavitTitle = styled.h3`
  text-align: center;
  font-size: 14px;
  font-weight: bold;
  margin: 20px 0 15px 0;
`;

const FormRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  gap: 10px;
  
  label {
    font-weight: 500;
    white-space: nowrap;
  }
  
  input, select {
    flex: 1;
    padding: 5px 8px;
    border: 1px solid #ccc;
    border-radius: 3px;
    font-size: 13px;
    
    &:focus {
      outline: none;
      border-color: #6cb288;
    }
  }
`;

const InlineFormGroup = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin: 0 5px;
  
  input {
    width: 120px;
    padding: 3px 6px;
    border: none;
    border-bottom: 1px solid #333;
    font-size: 13px;
    
    &:focus {
      outline: none;
      border-bottom-color: #6cb288;
    }
  }
  
  &.age input {
    width: 60px;
  }
  
  &.occupation input {
    width: 150px;
  }
  
  &.date-day {
    input {
      width: 40px;
      text-align: center;
    }
  }
  
  &.date-year {
    input {
      width: 60px;
      text-align: center;
    }
  }
`;

const TextAreaField = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 3px;
  font-size: 13px;
  resize: vertical;
  margin-top: 5px;
  
  &:focus {
    outline: none;
    border-color: #6cb288;
  }
`;

const AffidavitContent = styled.div`
  margin-top: 20px;
  text-align: justify;
  font-size: 13px;
  line-height: 1.8;
  
  p {
    margin-bottom: 15px;
    text-indent: 30px;
  }
  
  ol {
    list-style-type: lower-alpha;
    padding-left: 40px;
    
    li {
      margin-bottom: 12px;
      text-align: justify;
    }
  }
`;

const ClosingStatement = styled.div`
  margin-top: 30px;
  margin-bottom: 40px;
  font-size: 13px;
  line-height: 1.6;
`;

const SignatureSection = styled.div`
  margin-top: 60px;
  
  .signature-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;
    
    .left {
      flex: 1;
    }
    
    .right {
      text-align: right;
      font-style: italic;
      font-weight: 500;
    }
  }
  
  .date-place-row {
    display: flex;
    gap: 40px;
    margin-bottom: 10px;
    
    .field {
      display: flex;
      align-items: center;
      gap: 10px;
      
      label {
        font-weight: 500;
      }
      
      input {
        flex: 1;
        padding: 3px 6px;
        border: none;
        border-bottom: 1px solid #333;
        font-size: 13px;
        
        &:focus {
          outline: none;
          border-bottom-color: #6cb288;
        }
      }
    }
  }
`;

// Loading Modal Styled Components
const LoadingModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(5px);
`;

const LoadingModalContent = styled.div`
  background: white;
  padding: 40px;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  max-width: 400px;
  width: 90%;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #28a745;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.h3`
  color: #333;
  margin: 0 0 10px 0;
  font-size: 18px;
  font-weight: 600;
`;

const LoadingSubText = styled.p`
  color: #666;
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
`;

// Define required fields for validation across all pages
interface RequiredField {
  fieldName: string;
  label: string;
  page: number;
  pageName: string;
  owner: 'main' | 'partner';
  partnerIndex?: number;
  partnerName?: string;
}

const REQUIRED_FIELDS_CONFIG: Array<{
  fieldName: string;
  label: string;
  page: number;
  pageName: string;
  isPartnerField?: boolean;
}> = [
  // Page 1 - Main Applicant (using actual field names from ApplicationFormPage1.tsx)
  { fieldName: 'nameInBlocks', label: 'Full Name of Applicant', page: 1, pageName: 'Personal Details' },
  { fieldName: 'fatherOrHusbandName', label: 'Father/Mother/Husband Name', page: 1, pageName: 'Personal Details' },
  { fieldName: 'dateOfBirth', label: 'Date of Birth', page: 1, pageName: 'Personal Details' },
  { fieldName: 'aadhaarNumber', label: 'Aadhaar Number', page: 1, pageName: 'Personal Details' },
  { fieldName: 'panNumberStatutory', label: 'PAN Card Number', page: 1, pageName: 'Personal Details' },
  { fieldName: 'personalMobile', label: 'Mobile Number', page: 1, pageName: 'Personal Details' },
  { fieldName: 'emailId', label: 'Email ID', page: 1, pageName: 'Personal Details' },
  { fieldName: 'currentAddress', label: 'Current Address', page: 1, pageName: 'Personal Details' },
  { fieldName: 'permanentAddress', label: 'Permanent Address', page: 1, pageName: 'Personal Details' },
  // Page 2 - Land/Plot Details (using actual field names from ApplicationFormPage2.tsx)
  { fieldName: 'landCompleteAddress', label: 'Complete Land/Plot Address', page: 2, pageName: 'Land Details' },
  { fieldName: 'landOwnershipStatus', label: 'Land Ownership Status', page: 2, pageName: 'Land Details' },
  // Page 3 - Declaration
  { fieldName: 'declarationPlace', label: 'Declaration Place', page: 3, pageName: 'Declaration' },
  { fieldName: 'declarationDate', label: 'Declaration Date', page: 3, pageName: 'Declaration' },
  { fieldName: 'signatureFile', label: 'Signature Upload', page: 3, pageName: 'Declaration' },
];

interface FormData {
  applicationNoOfficeUse: string;
  // Fields from all pages for validation (using actual field names)
  nameInBlocks?: string;
  fatherOrHusbandName?: string;
  dateOfBirth?: string;
  aadhaarNumber?: string;
  panNumberStatutory?: string;
  personalMobile?: string;
  emailId?: string;
  permanentAddress?: string;
  landCompleteAddress?: string;
  landOwnershipStatus?: string;
  declarationPlace?: string;
  declarationDate?: string;
  signatureFile?: File | string | null;
  partnerDirectorDetails?: Array<{
    id: string;
    nameInBlocks: string;
    fatherOrHusbandName?: string;
    dateOfBirth?: string;
    aadhaarNumber?: string;
    panNumberStatutory?: string;
    [key: string]: any;
  }>;
  dealershipType?: string;
  // POA fields
  poaAffidavitTitle: string;
  poaAffidavitName: string;
  poaAffidavitAge: string;
  poaAffidavitOccupation: string;
  poaAffidavitResidingAt: string;
  poaPropertyDescription: string;
  poaDate: string;
  poaSubRegistrar: string;
  poaSerialNumber: string;
  poaHolderName: string;
  poaDayOf: string;
  poaYear: string;
  poaPlace: string;
  poaDateSigned: string;
  errors?: {
    poaAffidavitName?: string;
    poaAffidavitAge?: string;
    poaAffidavitOccupation?: string;
    poaAffidavitResidingAt?: string;
    poaPropertyDescription?: string;
    poaDate?: string;
    poaSubRegistrar?: string;
    poaSerialNumber?: string;
    poaHolderName?: string;
  };
}

interface ApplicationFormPage14Props {
  formData?: any;
  isViewMode?: boolean;
}

const initialState: FormData = {
  applicationNoOfficeUse: '',
  poaAffidavitTitle: 'Mr.',
  poaAffidavitName: '',
  poaAffidavitAge: '',
  poaAffidavitOccupation: '',
  poaAffidavitResidingAt: '',
  poaPropertyDescription: '',
  poaDate: '',
  poaSubRegistrar: '',
  poaSerialNumber: '',
  poaHolderName: '',
  poaDayOf: '',
  poaYear: '',
  poaPlace: '',
  poaDateSigned: '',
  errors: {}
};

const ApplicationFormPage14: React.FC<ApplicationFormPage14Props> = ({ isViewMode = false, formData: propFormData }) => {
  const formRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Application lock status (locked after payment or manually locked by admin) - declare early
  const [isApplicationLocked, setIsApplicationLocked] = useState<boolean>(false);

  const [applicationId, setApplicationId] = useState<string>('');
  const isNewlyGeneratedIdRef = useRef<boolean>(false);
  
  // Zoom state
  const [zoomScale, setZoomScale] = useState<number>(1.0);
  
  // Check if Fill Dummy Data button should be shown (controlled from Admin Controls, default OFF)
  const [showDummyDataButton, setShowDummyDataButton] = useState<boolean>(false);
  
  // Fetch showDummyDataButton setting from backend
  useEffect(() => {
    const fetchDummyDataSetting = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const API_BASE = API_URL === '/api' ? '' : API_URL;
        const response = await fetch(`${API_BASE}/api/public/settings`);
        if (response.ok) {
          const data = await response.json();
          const settings = data.settings || data;
          if (settings && typeof settings.showDummyDataButton === 'boolean') {
            setShowDummyDataButton(settings.showDummyDataButton);
          }
        }
      } catch (error) {
        console.error('Error fetching dummy data button setting:', error);
      }
    };
    fetchDummyDataSetting();
  }, []);
  
  // Form data state
  const [formData, setFormData] = useState<FormData>(propFormData || initialState);
  const [applicationStatus, setApplicationStatus] = useState<string | null>(null);

  // State for save indicator
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveMessage, setSaveMessage] = useState<string>('Saved');
  const [showSaveIndicator, setShowSaveIndicator] = useState<boolean>(false);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  
  // State for PDF generation modal
  const [isPdfGenerating, setIsPdfGenerating] = useState<boolean>(false);
  const [compressionProgress, setCompressionProgress] = useState<number>(0);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalFileUrl, setModalFileUrl] = useState<string | null>(null);
  const [modalFileName, setModalFileName] = useState<string | null>(null);
  const [modalFileType, setModalFileType] = useState<string | null>(null);
  
  // Loading modal state
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Save modal state for navigation
  const [showSaveModal, setShowSaveModal] = useState<boolean>(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [showBrochureModal, setShowBrochureModal] = useState<boolean>(false);

  // Missing fields for validation
  const [missingFields, setMissingFields] = useState<RequiredField[]>([]);
  
  // Appendix visibility based on admin settings for the dealership type
  const [visibleAppendixes, setVisibleAppendixes] = useState<string[]>(['A', 'B', 'D', 'E', 'F']);
  
  // Payment button visibility (controlled by admin)
  const [hidePaymentButton, setHidePaymentButton] = useState<boolean>(false);

  // Save timeout reference for debouncing
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Global progress bar (across Page1+2+3+4) — Page5/14 is info-only, doesn't contribute
  const formProgress = useFormProgress(formData);

  // Calculate missing fields whenever formData changes
  useEffect(() => {
    const missing: RequiredField[] = [];
    
    // Check main applicant fields
    REQUIRED_FIELDS_CONFIG.forEach(fieldConfig => {
      const value = formData[fieldConfig.fieldName as keyof FormData];
      const isEmpty = value === undefined || value === null || value === '' || 
        (typeof value === 'string' && value.trim() === '');
      
      // Special case: permanentAddress is not required if permanentAddressSameAsCurrent is checked
      if (fieldConfig.fieldName === 'permanentAddress' && (formData as any).permanentAddressSameAsCurrent) {
        return; // Skip - permanent address is same as current
      }
      
      if (isEmpty) {
        missing.push({
          fieldName: fieldConfig.fieldName,
          label: fieldConfig.label,
          page: fieldConfig.page,
          pageName: fieldConfig.pageName,
          owner: 'main'
        });
      }
    });

    // Check partner fields if applicable
    if (formData.partnerDirectorDetails && formData.partnerDirectorDetails.length > 0) {
      const partnerLabel = (formData.dealershipType === 'LLP' || formData.dealershipType === 'Partnership') ? 'Partner' : 'Director';
      
      const partnerRequiredFields = [
        { fieldName: 'nameInBlocks', label: 'Full Name', page: 1 },
        { fieldName: 'fatherOrHusbandName', label: 'Father/Husband Name', page: 1 },
        { fieldName: 'dateOfBirth', label: 'Date of Birth', page: 1 },
        { fieldName: 'aadhaarNumber', label: 'Aadhaar Number', page: 1 },
        { fieldName: 'panNumberStatutory', label: 'PAN Card Number', page: 1 },
        { fieldName: 'currentAddress', label: 'Current Address', page: 1 },
        { fieldName: 'permanentAddress', label: 'Permanent Address', page: 1 },
        { fieldName: 'photo', label: 'Photo Upload', page: 1 },
        { fieldName: 'isMinimumEducationMet', label: 'Class 10 (High School) Question', page: 1 },
        { fieldName: 'nationality', label: 'Nationality', page: 1 },
        { fieldName: 'gender', label: 'Gender', page: 1 },
        { fieldName: 'maritalStatus', label: 'Marital Status', page: 1 },
        { fieldName: 'signatureFile', label: 'Signature Upload', page: 3 },
      ];

      formData.partnerDirectorDetails.forEach((partner, index) => {
        const partnerName = partner.nameInBlocks || `${partnerLabel} #${index + 2}`;
        
        // Check basic required fields
        partnerRequiredFields.forEach(field => {
          const value = partner[field.fieldName];
          const isEmpty = value === undefined || value === null || value === '' ||
            (typeof value === 'string' && value.trim() === '');
          
          // Special case: permanentAddress is not required if permanentAddressSameAsCurrent is checked
          if (field.fieldName === 'permanentAddress' && partner.permanentAddressSameAsCurrent) {
            return; // Skip - permanent address is same as current
          }
          
          if (isEmpty) {
            missing.push({
              fieldName: field.fieldName,
              label: field.label,
              page: field.page,
              pageName: field.page === 3 ? 'Declaration & Signature' : 'Personal Details',
              owner: 'partner',
              partnerIndex: index,
              partnerName: partnerName
            });
          }
        });
        
        // Check if Class 10 question is answered "No" (ineligible)
        if (partner.isMinimumEducationMet === 'No') {
          missing.push({
            fieldName: 'isMinimumEducationMet',
            label: 'Class 10 Qualification Required (Answered No)',
            page: 1,
            pageName: 'Personal Details',
            owner: 'partner',
            partnerIndex: index,
            partnerName: partnerName
          });
        }
        
        // Check Educational Qualification - at least one document required
        const hasEduDoc = 
          (partner.eduSscCertificateFile && partner.eduSscBoard) ||
          (partner.eduHscCertificateFile && partner.eduHscBoard) ||
          (partner.eduGraduateCertificateFile && partner.eduGraduateBoard) ||
          (partner.eduOthersCertificateFile && partner.eduOthersBoard);
        
        if (!hasEduDoc) {
          missing.push({
            fieldName: 'educationalQualification',
            label: 'Educational Qualification Document',
            page: 1,
            pageName: 'Educational Qualification',
            owner: 'partner',
            partnerIndex: index,
            partnerName: partnerName
          });
        }
        
        // Check Business/Employment Details
        const hasBusinessDetails = partner.presentOccupation && partner.businessEmploymentDetails && partner.totalWorkExperienceYears;
        if (!hasBusinessDetails) {
          missing.push({
            fieldName: 'businessEmploymentDetails',
            label: 'Business/Employment Details',
            page: 1,
            pageName: 'Business/Employment Details',
            owner: 'partner',
            partnerIndex: index,
            partnerName: partnerName
          });
        }
        
        // Check Financial Capability
        const hasFinancialCapability = partner.annualIncome && partner.netWorthAtApplication && partner.sourceOfInvestment && partner.abilityToMeetRequiredInvestment;
        if (!hasFinancialCapability) {
          missing.push({
            fieldName: 'financialCapability',
            label: 'Financial Capability Details',
            page: 1,
            pageName: 'Financial Capability',
            owner: 'partner',
            partnerIndex: index,
            partnerName: partnerName
          });
        }
        
        // Check Financial Capability Document - at least one document required
        const hasFinDoc = partner.finDocProjectReportFile || partner.finDocBankStatementFile ||
          partner.finDocFdTdReceiptsFile || partner.finDocInvestmentCertificatesFile ||
          partner.finDocMutualFundStatementFile || partner.finDocDematStatementFile ||
          partner.finDocCaCertificateFile || partner.finDocOwnershipDocsFile;
        if (!hasFinDoc) {
          missing.push({
            fieldName: 'financialCapabilityDocument',
            label: 'Financial Capability Document',
            page: 1,
            pageName: 'Financial Capability',
            owner: 'partner',
            partnerIndex: index,
            partnerName: partnerName
          });
        }
        
        // Check Appendix uploads for partners - ONLY check appendixes that are visible based on admin settings
        const appendixFields = [
          { key: 'A', fieldName: 'affidavitFile', label: 'Appendix A - Affidavit' },
          { key: 'B', fieldName: 'appendixBFile', label: 'Appendix B - Individual/Partner Affidavit' },
          { key: 'C', fieldName: 'appendixCFile', label: 'Appendix C - Non-Individual Entity Affidavit' },
          { key: 'D', fieldName: 'appendixDFile', label: 'Appendix D - Property/Land Ownership' },
          { key: 'E', fieldName: 'appendixEFile', label: 'Appendix E - Advocate Certificate' },
          { key: 'F', fieldName: 'appendixFFile', label: 'Appendix F - Land Owner Consent/NOC' },
        ];
        
        appendixFields.forEach(appendix => {
          // Only check if this appendix is visible based on admin Appendix Logic settings
          if (!visibleAppendixes.includes(appendix.key)) {
            return; // Skip - this appendix is not required for this dealership type
          }
          
          const value = partner[appendix.fieldName];
          const isEmpty = value === undefined || value === null || value === '';
          
          if (isEmpty) {
            missing.push({
              fieldName: appendix.fieldName,
              label: appendix.label,
              page: 1,
              pageName: 'Appendix Uploads',
              owner: 'partner',
              partnerIndex: index,
              partnerName: partnerName
            });
          }
        });
      });
    }

    setMissingFields(missing);
  }, [formData, visibleAppendixes]);

  // Handle navigation to specific field
  const handleNavigateToField = useCallback((page: number, fieldName: string) => {
    // Store the field to scroll to in sessionStorage
    sessionStorage.setItem('scrollToField', fieldName);
    
    // Navigate to the appropriate page
    const pageRoutes: { [key: number]: string } = {
      1: '/form/page1',
      2: '/form/page2',
      3: '/form/page3',
      4: '/form/page4',
    };
    
    const route = pageRoutes[page] || '/form/page1';
    navigate(route, { state: { applicationId, scrollToField: fieldName } });
  }, [navigate, applicationId]);

  // Effect to initialize or generate applicationId
  useEffect(() => {
    // If formData is passed as a prop, use its ID and skip the rest of the effect.
    if (propFormData?.applicationNoOfficeUse) {
      setApplicationId(propFormData.applicationNoOfficeUse);
      return;
    }

    let currentApplicationId = location.state?.applicationId || sessionStorage.getItem('currentApplicationId');
    let source = '';

    if (currentApplicationId) {
      source = location.state?.applicationId ? 'location.state' : 'sessionStorage';
      console.log(`[ApplicationFormPage14 Effect InitID] Found existing Application ID: ${currentApplicationId} from ${source}`);
      setApplicationId(currentApplicationId);
      if (formData.applicationNoOfficeUse !== currentApplicationId) {
        setFormData(prev => ({ ...prev, applicationNoOfficeUse: currentApplicationId }));
      }
    } else {
      const newId = generateApplicationId();
      console.log(`[ApplicationFormPage14 Effect InitID] No existing ID found. Generated new Application ID: ${newId}`);
      setApplicationId(newId);
      sessionStorage.setItem('currentApplicationId', newId);
      isNewlyGeneratedIdRef.current = true;
      setFormData(prev => ({ ...prev, applicationNoOfficeUse: newId }));
    }
  }, [location.state, propFormData]);

  // Effect to load existing application data when resuming a draft
  useEffect(() => {
    // If formData is passed as a prop, don't load draft data from the server.
    if (propFormData) {
      return;
    }

    const loadDraftData = async () => {
      if (applicationId && !isNewlyGeneratedIdRef.current && !isViewMode) {
        console.log(`[ApplicationFormPage14 LoadDraft] Attempting to load draft data for ID: ${applicationId}`);
        try {
          const appData = await fetchApplicationDetails(applicationId);
          if (appData && appData.formData) {
            const loadedFormData = {
              ...appData.formData,
              // Handle any file fields if needed
            };
            setFormData(loadedFormData);
            if (typeof appData.status !== 'undefined') {
              setApplicationStatus(appData.status);
            }
            console.log(`[ApplicationFormPage14 LoadDraft] Successfully loaded draft data for ID: ${applicationId}`, appData);
          } else {
            console.warn(`[ApplicationFormPage14 LoadDraft] No application data or formData found for ID: ${applicationId}`, appData);
          }
        } catch (error) {
          console.error(`[ApplicationFormPage14 LoadDraft] Error loading draft data for ${applicationId}:`, error);
        }
      }
    };

    loadDraftData();
  }, [applicationId, isViewMode, propFormData]);

  // Effect to check if application is locked (after payment or manually locked by admin)
  useEffect(() => {
    const checkLockStatus = async () => {
      if (!applicationId) return;
      try {
        const response = await axios.get(`${API_BASE}/api/settings/application-lock-status/${applicationId}`);
        if (response.data.success && response.data.isLocked) {
          setIsApplicationLocked(true);
          console.log(`[AppFormPage14] Application ${applicationId} is locked: ${response.data.reason}`);
        } else {
          setIsApplicationLocked(false);
        }
      } catch (error) {
        console.error('[AppFormPage14] Error checking application lock status:', error);
      }
    };
    checkLockStatus();
  }, [applicationId]);

  // Poll payment button status every 5 seconds for real-time updates
  useEffect(() => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const API_BASE = API_URL === '/api' ? '' : API_URL;
    
    const checkPaymentButtonStatus = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/settings/payment-button-status`);
        if (response.ok) {
          const data = await response.json();
          setHidePaymentButton(data.hidePaymentButton === true);
        }
      } catch (error) {
        console.error('Error checking payment button status:', error);
      }
    };
    
    // Check immediately on mount
    checkPaymentButtonStatus();
    
    // Poll every 5 seconds
    const intervalId = setInterval(checkPaymentButtonStatus, 5000);
    
    return () => clearInterval(intervalId);
  }, []);

  // Fetch appendix visibility settings when dealershipType changes
  useEffect(() => {
    const fetchAppendixLogic = async () => {
      if (!formData.dealershipType) {
        setVisibleAppendixes(['A', 'B', 'D', 'E', 'F']); // Default
        return;
      }
      
      try {
        const RAW_API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? 'https://backend.mngldodo.site' : 'http://localhost:5000');
        const API_BASE = RAW_API_URL.endsWith('/api') ? RAW_API_URL.slice(0, -4) : RAW_API_URL;
        const url = `${API_BASE}/api/appendix-logic/${encodeURIComponent(formData.dealershipType)}`;
        console.log('[Page14 AppendixLogic] Fetching from:', url);
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          console.log('[Page14 AppendixLogic] Response:', data);
          if (data.success && data.visibleAppendixes) {
            setVisibleAppendixes(data.visibleAppendixes);
            console.log('[Page14 AppendixLogic] Set visibleAppendixes to:', data.visibleAppendixes);
          }
        }
      } catch (error) {
        console.error('[Page14] Error fetching appendix logic:', error);
      }
    };
    fetchAppendixLogic();
  }, [formData.dealershipType]);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const handleOpenFilePreview = (file: File | string | null, defaultFileName: string = 'file') => {
    console.log('[handleOpenFilePreview] Called with file:', file, 'defaultFileName:', defaultFileName);
    if (!file) {
      console.log('[handleOpenFilePreview] File is null, returning.');
      return;
    }

    console.log('[handleOpenFilePreview] Current API_BASE_URL:', API_BASE_URL);

    let url: string | null = null;
    let name: string | null = null;
    let type: string | null = null;

    if (typeof file === 'string') {
      console.log('[handleOpenFilePreview] File is a string:', file);

      const rawApiBaseUrl = process.env.REACT_APP_API_URL || '/api';
      let serverBaseForFiles = window.location.origin;
      if (rawApiBaseUrl.startsWith('http')) {
        try {
          const urlObject = new URL(rawApiBaseUrl);
          serverBaseForFiles = `${urlObject.protocol}//${urlObject.host}`;
        } catch (e) {
          console.warn('[handleOpenFilePreview] Could not parse REACT_APP_API_URL, using window.origin instead:', e);
          serverBaseForFiles = window.location.origin;
        }
      }

      let normalizedPath = file;
      if (file.includes('://')) {
        try { normalizedPath = new URL(file).pathname; } catch { normalizedPath = file; }
      }
      if (!normalizedPath.startsWith('/')) {
        normalizedPath = normalizedPath.includes('/') ? `/${normalizedPath}` : `/uploads/${normalizedPath}`;
      }

      url = `${serverBaseForFiles}${normalizedPath}`;
      name = normalizedPath.split('/').pop() || defaultFileName;

      const lower = name.toLowerCase();
      if (lower.endsWith('.pdf')) {
        type = 'application/pdf';
      } else if (lower.match(/\.(jpeg|jpg|gif|png)$/i)) {
        const ext = lower.split('.').pop();
        type = `image/${ext === 'jpg' ? 'jpeg' : ext}`;
      } else {
        type = 'application/octet-stream';
      }
      console.log(`[handleOpenFilePreview] Constructed URL: ${url}, Name: ${name}, Type: ${type}`);
    } else {
      console.log('[handleOpenFilePreview] File is a File object:', file);
      url = URL.createObjectURL(file);
      name = file.name;
      type = file.type;
      console.log('[handleOpenFilePreview] File object - Constructed URL:', url, 'Name:', name, 'Type:', type);
    }

    setModalFileUrl(url);
    setModalFileName(name);
    setModalFileType(type);
    setIsModalOpen(true);
    console.log('[handleOpenFilePreview] Modal state set. isOpen:', true, 'Url:', url);
  };

  const handleDownloadPdf = async () => {
    setIsPdfGenerating(true);
    setCompressionProgress(0);
    const formElement = formRef.current;
    if (!formElement) {
      alert('Form content is not available for PDF export.');
      setIsPdfGenerating(false);
      return;
    }

    try {
      // Create a container for the cloned form
      const cloneContainer = document.createElement('div');
      cloneContainer.style.position = 'absolute';
      cloneContainer.style.left = '-9999px';
      cloneContainer.style.top = '-9999px';
      cloneContainer.style.width = `${formElement.offsetWidth}px`;
      document.body.appendChild(cloneContainer);
      
      // Clone the form and append to the container
      const formClone = formElement.cloneNode(true) as HTMLElement;
      cloneContainer.appendChild(formClone);

      // Process form inputs
      const inputs = formClone.querySelectorAll('input[type="text"], input[type="number"], textarea');
      inputs.forEach(input => {
        const typedInput = input as HTMLInputElement | HTMLTextAreaElement;
        const value = typedInput.value;
        if (value) {
          const span = document.createElement('span');
          span.textContent = value;
          span.style.borderBottom = '1px solid #333';
          span.style.padding = '2px 5px';
          span.style.margin = '0 5px';
          span.style.fontSize = 'inherit';
          span.style.fontFamily = 'inherit';
          span.style.display = 'inline-block';
          span.style.whiteSpace = 'pre-wrap';
          span.style.wordBreak = 'break-word';
          span.style.color = window.getComputedStyle(typedInput).color;
          typedInput.parentNode?.replaceChild(span, typedInput);
        }
      });

      // Process checkboxes
      const checkboxes = formClone.querySelectorAll('input[type="checkbox"]');
      checkboxes.forEach(checkbox => {
        const typedCheckbox = checkbox as HTMLInputElement;
        const span = document.createElement('span');
        span.textContent = typedCheckbox.checked ? '☑' : '☐';
        span.style.fontSize = 'inherit';
        span.style.fontFamily = 'inherit';
        span.style.display = 'inline-block';
        span.style.margin = '0 5px';
        typedCheckbox.parentNode?.replaceChild(span, typedCheckbox);
      });

      // Hide buttons
      const buttons = formClone.querySelectorAll('button');
      buttons.forEach(btn => (btn.style.visibility = 'hidden'));

      // Generate canvas with higher scale for better quality
      const canvas = await html2canvas(formClone, {
        scale: 3, // Higher scale for better quality, matching Page5
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#FFFFFF',
        width: formClone.scrollWidth,
        height: formClone.scrollHeight
      });
      
      // Remove the clone container from the DOM
      document.body.removeChild(cloneContainer);
      
      // Create PDF with appropriate orientation
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'l' : 'p',
        unit: 'pt',
        format: [canvas.width, canvas.height],
      });
      
      // Use JPEG compression for smaller file size
      const imgData = canvas.toDataURL('image/jpeg', 0.5);
      pdf.addImage(imgData, 'JPEG', 0, 0, canvas.width, canvas.height);
      
      // Simulate compression progress
      for (let i = 0; i <= 100; i += 10) {
        setCompressionProgress(i);
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      // Save the PDF with application ID in the filename
      pdf.save(`application-form-${applicationId || 'draft'}.pdf`);
    } catch (error) {
      console.error('PDF Generation Error:', error);
      alert("Error generating PDF. Please try again.");
    } finally {
      setIsPdfGenerating(false);
      setCompressionProgress(0);
    }
  };

  // Function to save data to database
  const saveToDatabaseCallback = useCallback(async (data: typeof formData, isDraft: boolean, anId?: string) => {
    const idToUse = anId || applicationId;

    if (!idToUse) {
      console.error('Application ID is missing, cannot save.');
      setSaveMessage('Error: Application ID missing.');
      setIsSaving(false);
      return;
    }

    if (!isOnline) {
      console.warn('Offline: Save operation skipped for application ID:', idToUse);
      setSaveMessage('Offline: Changes will be saved when back online.');
      return;
    }

    console.log(`[SaveToDB] Attempting to save. ID: ${idToUse}, isDraft: ${isDraft}`);
    setIsSaving(true);
    const currentSaveMessage = isDraft ? 'Saving Draft...' : 'Submitting Application...';
    setSaveMessage(currentSaveMessage);
    setShowSaveIndicator(true);
    
    try {
      const dataToSave = {
        ...data,
        // Handle any file fields if needed
      };
      
      const response = await saveFormData(idToUse, dataToSave, isDraft);
      
      if (response.success) {
        const successMsg = isDraft ? 'Draft saved successfully' : 'Application submitted successfully';
        console.log(`[SaveToDB] Success for ID: ${idToUse}. Message: ${successMsg}`);
        setSaveMessage(successMsg);
        if (isDraft && isNewlyGeneratedIdRef.current) {
          isNewlyGeneratedIdRef.current = false;
          console.log('[SaveToDB] Reset isNewlyGeneratedIdRef for ID:', idToUse);
        }
        setTimeout(() => setShowSaveIndicator(false), 2000);
        
        // If final submission, navigate to success page
        if (!isDraft) {
          setTimeout(() => {            
			// NEW LINE
			navigate(`/payment-details/${idToUse}`, { state: { applicationId: idToUse } });
          }, 1500);
        }
      } else {
        setSaveMessage('Error saving');
        setTimeout(() => setShowSaveIndicator(false), 3000);
      }
    } catch (error) {
      console.error('Error saving form data:', error);
      setSaveMessage('Error saving');
      setTimeout(() => setShowSaveIndicator(false), 3000);
    } finally {
      setIsSaving(false);
    }
  }, [applicationId, isOnline, navigate]);

  // Effect for initial auto-save of a newly generated ID
  useEffect(() => {
    if (applicationId && isNewlyGeneratedIdRef.current && isOnline && Object.keys(formData).some(key => formData[key as keyof FormData] !== '' && key !== 'errors' && key !== 'applicationNoOfficeUse')) {
      console.log(`[ApplicationFormPage14 Effect AutoSave] Application ID ${applicationId} is newly generated. Attempting to save as draft.`);
      saveToDatabaseCallback(formData, true, applicationId);
    }
  }, [applicationId, formData, saveToDatabaseCallback, isOnline]);

  const handleFillDummyData = () => {
    console.log('[ApplicationFormPage14 FillDummyData] Filling form with dummy data.');
    const currentDate = new Date();
    setFormData(prevData => ({
      ...prevData,
      applicationNoOfficeUse: prevData.applicationNoOfficeUse || applicationId || '',
      poaAffidavitTitle: 'Mr.',
      poaAffidavitName: 'John Doe',
      poaAffidavitAge: '45',
      poaAffidavitOccupation: 'Business Owner',
      poaAffidavitResidingAt: '456 Park Avenue, Pune, Maharashtra 411004',
      poaPropertyDescription: 'Plot No. 78, Survey No. 456/7, Area: 3000 sq.ft., Located at Baner, Pune',
      poaDate: '15/03/2023',
      poaSubRegistrar: 'Pune Sub-Registrar Office',
      poaSerialNumber: 'SR-2023-456789',
      poaHolderName: 'Jane Doe',
      poaDayOf: currentDate.getDate().toString(),
      poaYear: currentDate.getFullYear().toString().slice(-2),
      poaPlace: 'Pune',
      poaDateSigned: currentDate.toLocaleDateString('en-GB'),
      errors: {},
    }));
    setSaveMessage('Dummy data filled. Save to persist.');
    setShowSaveIndicator(true);
    setTimeout(() => {
      setShowSaveIndicator(false);
    }, 3000);
  };

  const debouncedSave = useCallback((newData: FormData) => {
    console.log('[DebouncedSave] Invoked. Clearing previous timeout if any.');
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      console.log('[DebouncedSave] Timeout executed. Checking conditions to save.');
      if (applicationId) {
        console.log(`[DebouncedSave] Conditions met. ApplicationId: ${applicationId}. Proceeding to save.`);
        console.log(`[DebouncedSave] Triggering auto-save (as draft) for ID: ${applicationId}`);
        saveToDatabaseCallback({ ...newData, applicationNoOfficeUse: applicationId }, true, applicationId);
      } else {
        console.log(`[DebouncedSave] Conditions NOT met. Skipping save. ApplicationId: ${applicationId}`);
      }
    }, 1000);
  }, [saveToDatabaseCallback, applicationId]);

  // Removed interval-based auto-save - using navigation-based save modal instead

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);
    // Removed auto-save on every keystroke - use SAVE DRAFT button or navigation modal instead
  };

  // Handle save modal actions for navigation
  const handleSaveAndNavigate = async () => {
    setShowSaveModal(false);
    
    if (applicationId && isOnline) {
      console.log('[Navigation Page14] Saving draft before navigating');
      await saveToDatabaseCallback(formData, true, applicationId);
      
      // Wait 1.5 seconds so user can see the "Draft saved successfully" message
      await new Promise(resolve => setTimeout(resolve, 1500));
    }
    
    if (pendingNavigation) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const handleSkipSaveAndNavigate = () => {
    setShowSaveModal(false);
    if (pendingNavigation) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  // Handle navigation from vertical nav bar - show save modal
  const handleNavBarNavigation = (targetPath: string) => {
    // Skip save modal if application is locked
    if (applicationId && isOnline && !isApplicationLocked) {
      setPendingNavigation(targetPath);
      setShowSaveModal(true);
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
      navigate(targetPath, { state: { applicationId } });
    }
  };

  const handleZoomIn = () => {
    setZoomScale(prev => Math.min(prev + 0.1, 1.5));
  };
  
  const handleZoomOut = () => {
    setZoomScale(prev => Math.max(prev - 0.1, 0.6));
  };

  const handleSubmitApplication = () => {
    // Use the same missingFields validation as the UI display
    if (missingFields.length > 0) {
      alert(`Please complete ${missingFields.length} missing field(s) before proceeding to payment.`);
      return;
    }

    // Show loading modal
    setIsLoading(true);

    // Submit the application (not as draft)
    saveToDatabaseCallback(formData, false, applicationId);

    // Simulate loading and redirect after 3 seconds
    setTimeout(() => {
      setIsLoading(false);
      // Add your redirect logic here - for example:
      // navigate('/payment') or window.location.href = 'payment-url'
      console.log('Application submitted successfully - ready for payment redirect');
      navigate(`/payment-details/${applicationId}`, { state: { applicationId: applicationId } });
    }, 3000);
  };

  const isPaymentComplete = (applicationStatus || '').toLowerCase() === 'payment_complete';

  // Form progress is now calculated by the shared useFormProgress hook (global across Page1+2+3)
  // Page14 (Application Summary) is info-only and does NOT contribute to progress calculation

  // Effect for online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <>
      {/* Progress bar */}
      <ProgressBarContainer>
        <ProgressBarFill progress={formProgress} />
      </ProgressBarContainer>
      <ProgressLabel>Page 5/5 • {Math.round(formProgress)}% complete</ProgressLabel>
      
      {/* Zoom controls */}
      <ZoomControlsContainer>
        <ZoomButton onClick={handleZoomIn}>+</ZoomButton>
        <ZoomButton onClick={handleZoomOut}>−</ZoomButton>
      </ZoomControlsContainer>
      
      {/* Save indicator */}
      <SaveIndicator visible={showSaveIndicator}>
        {saveMessage}
      </SaveIndicator>

      {!isOnline && (
        <OfflineIndicator>
          You are currently offline. Changes will not be saved until you reconnect.
        </OfflineIndicator>
      )}
      {/* Application Locked Banner */}
      {isApplicationLocked && !isViewMode && (
        <div style={{
          position: 'fixed',
          top: '40px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(254, 243, 199, 0.92)',
          backdropFilter: 'blur(8px)',
          border: '2px solid #f59e0b',
          borderRadius: '12px',
          padding: '10px 20px',
          zIndex: 9998,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          boxShadow: '0 4px 20px rgba(245, 158, 11, 0.25)',
          maxWidth: '90%'
        }}>
          <span style={{ fontSize: '1.3rem' }}>🔒</span>
          <div>
            <div style={{ fontWeight: 600, color: '#92400e', fontSize: '0.9rem' }}>
              Application Locked
            </div>
            <div style={{ color: '#78350f', fontSize: '0.75rem' }}>
              This application is locked. You can view data but cannot make changes.
            </div>
          </div>
        </div>
      )}
      
      <FloatingBackButton onClick={() => { window.scrollTo({ top: 0, behavior: 'auto' }); navigate('/form/page4'); }} aria-label="Back to previous page">
        ←
      </FloatingBackButton>
      <div style={{ position: 'fixed', top: '80px', left: '30px', zIndex: 1001 }}>
        <button 
          onClick={() => setShowBrochureModal(true)}
          style={{ 
            backgroundColor: '#2196f3', 
            color: '#ffffff', 
            borderRadius: '4px', 
            padding: '8px 15px',
            border: '1px solid #1976d2',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}
        >
          📄 View Brochure
        </button>
      </div>
      
      {showDummyDataButton && (
        <div style={{ position: 'fixed', top: '80px', right: '30px', zIndex: 1001, display: 'block' }}>
          <button 
            onClick={handleFillDummyData} 
            style={{ 
              backgroundColor: '#ffc107', 
              color: '#212529', 
              borderRadius: '4px', 
              padding: '8px 15px',
              border: '1px solid #e0a800',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
            }}
          >
            Fill Dummy Data
          </button>
        </div>
      )}
      
      <FormPageNavigator currentPage={14} totalPages={14} targetFormRef={formRef} zoomScale={zoomScale} onNavigate={handleNavBarNavigation} />
      
      <PageWrapper scale={zoomScale}>
        <A4PageContainer ref={formRef}>
          <FormContentContainer>
            {/* Application Summary Section */}
            <div style={{ padding: '20px' }}>
              <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                <h2 style={{ 
                  color: '#1e40af', 
                  fontSize: '1.8em', 
                  fontWeight: 700, 
                  marginBottom: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px'
                }}>
                  📋 Application Summary
                </h2>
                <p style={{ color: '#6b7280', fontSize: '0.95em' }}>
                  Review your application status before proceeding to payment
                </p>
              </div>

              {/* Completion Status Card */}
              <div style={{
                background: missingFields.length === 0 
                  ? 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)' 
                  : 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                borderRadius: '16px',
                padding: '24px',
                marginBottom: '24px',
                border: missingFields.length === 0 ? '2px solid #22c55e' : '2px solid #f59e0b',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    background: missingFields.length === 0 ? '#22c55e' : '#f59e0b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '28px'
                  }}>
                    {missingFields.length === 0 ? '✓' : '⚠'}
                  </div>
                  <div>
                    <h3 style={{ 
                      margin: 0, 
                      fontSize: '1.3em', 
                      color: missingFields.length === 0 ? '#15803d' : '#b45309',
                      fontWeight: 700
                    }}>
                      {missingFields.length === 0 
                        ? 'Application Complete!' 
                        : `${missingFields.length} Field${missingFields.length > 1 ? 's' : ''} Missing`}
                    </h3>
                    <p style={{ 
                      margin: '4px 0 0 0', 
                      color: missingFields.length === 0 ? '#166534' : '#92400e',
                      fontSize: '0.95em'
                    }}>
                      {missingFields.length === 0 
                        ? 'You can now proceed to payment' 
                        : 'Please complete all required fields before payment'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Missing Fields List */}
              {missingFields.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ 
                    color: '#374151', 
                    fontSize: '1.1em', 
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    📝 Required Fields to Complete:
                  </h4>
                  
                  {/* Group by page */}
                  {Object.entries(
                    missingFields.reduce((acc: { [key: string]: typeof missingFields }, field) => {
                      const key = `Page ${field.page} - ${field.pageName}`;
                      if (!acc[key]) acc[key] = [];
                      acc[key].push(field);
                      return acc;
                    }, {})
                  ).map(([pageKey, fields]) => (
                    <div key={pageKey} style={{
                      background: '#ffffff',
                      borderRadius: '12px',
                      marginBottom: '12px',
                      border: '1px solid #e5e7eb',
                      overflow: 'hidden',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}>
                      <div style={{
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        color: 'white',
                        padding: '12px 16px',
                        fontWeight: 600,
                        fontSize: '0.95em'
                      }}>
                        {pageKey}
                      </div>
                      <div style={{ padding: '8px' }}>
                        {fields.map((field, idx) => (
                          <div
                            key={idx}
                            onClick={() => handleNavigateToField(field.page, field.fieldName)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '12px 16px',
                              margin: '4px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              background: '#fef2f2',
                              border: '1px solid #fecaca'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#fee2e2';
                              e.currentTarget.style.transform = 'translateX(4px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#fef2f2';
                              e.currentTarget.style.transform = 'translateX(0)';
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <span style={{ 
                                color: '#dc2626', 
                                fontSize: '16px',
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                background: '#fee2e2',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>✗</span>
                              <div>
                                <div style={{ fontWeight: 500, color: '#1f2937' }}>{field.label}</div>
                                <div style={{ fontSize: '0.8em', color: '#6b7280' }}>
                                  {field.owner === 'main' ? '👤 Main Applicant' : `👥 Partner #${(field.partnerIndex || 0) + 2}${field.partnerName ? ` (${field.partnerName})` : ''}`}
                                </div>
                              </div>
                            </div>
                            <div style={{
                              color: '#3b82f6',
                              fontSize: '0.85em',
                              fontWeight: 500,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              Go to field →
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Completed Fields Summary */}
              {missingFields.length === 0 && (
                <div style={{
                  background: hidePaymentButton ? '#fefce8' : '#f0fdf4',
                  borderRadius: '12px',
                  padding: '20px',
                  border: `1px solid ${hidePaymentButton ? '#fef08a' : '#bbf7d0'}`
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                    <span style={{ fontSize: '24px' }}>{hidePaymentButton ? '✅' : '🎉'}</span>
                    <h4 style={{ margin: 0, color: hidePaymentButton ? '#a16207' : '#15803d' }}>All Required Fields Completed</h4>
                  </div>
                  <p style={{ color: hidePaymentButton ? '#a16207' : '#166534', margin: 0, fontSize: '0.95em' }}>
                    {hidePaymentButton 
                      ? 'Your application is complete. Payment is currently unavailable. Please check back later.'
                      : 'Your application is ready for submission. Click the "PROCEED TO PAYMENT" button below to continue with the payment process.'}
                  </p>
                </div>
              )}

              {/* Application Info */}
              <div style={{
                background: '#f8fafc',
                borderRadius: '12px',
                padding: '20px',
                marginTop: '24px',
                border: '1px solid #e2e8f0'
              }}>
                <h4 style={{ margin: '0 0 12px 0', color: '#475569', fontSize: '0.95em' }}>
                  📄 Application Details
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', fontSize: '0.9em' }}>
                  <div>
                    <span style={{ color: '#64748b' }}>Application No:</span>
                    <span style={{ marginLeft: '8px', fontWeight: 600, color: '#1e293b' }}>{applicationId || 'Not generated'}</span>
                  </div>
                  <div>
                    <span style={{ color: '#64748b' }}>Applicant:</span>
                    <span style={{ marginLeft: '8px', fontWeight: 600, color: '#1e293b' }}>{formData.nameInBlocks || 'Not provided'}</span>
                  </div>
                </div>
              </div>
            </div>
          </FormContentContainer>
          <PageNumber>14</PageNumber>
        </A4PageContainer>
        
        <FilePreviewModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          fileUrl={modalFileUrl}
          fileName={modalFileName}
          fileType={modalFileType}
        />
      </PageWrapper>
      
      {/* WhatsApp Help Button */}
      <div style={{ position: 'fixed', right: '20px', bottom: '100px', zIndex: 999, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
        <span style={{ fontSize: '11px', fontWeight: 600, color: '#25D366', textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>Need Help!</span>
        <a 
          href="https://web.whatsapp.com/send?phone=919971874909&text=Hi%2C%20Need%20Help%20filling%20MNGL%20DODO%20form"
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'block', cursor: 'pointer', transition: 'transform 0.2s ease' }}
          onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
          title="Chat with us on WhatsApp"
        >
          <img src="/assets/images/whatsapp.png" alt="WhatsApp Help" style={{ }} />
        </a>
      </div>
      
	  <ActionButtonsContainer style={{ position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: '800px', zIndex: 1000, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
	    {!isViewMode && (
          <>
            {/* Show payment disabled message when admin has hidden the button */}
            {/* Hide PROCEED TO PAYMENT for locked applications or payment_complete status */}
            {(isApplicationLocked || isPaymentComplete) ? (
              <div style={{
                background: 'linear-gradient(135deg, rgba(219, 234, 254, 0.95) 0%, rgba(191, 219, 254, 0.95) 100%)',
                color: '#1e40af',
                padding: '10px 20px',
                borderRadius: '8px',
                fontSize: '0.9em',
                fontWeight: 500,
                textAlign: 'center',
                border: '1px solid #3b82f6',
                boxShadow: '0 2px 8px rgba(59, 130, 246, 0.15)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{ fontSize: '1.2em' }}>✅</span>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.95em' }}>
                    {isPaymentComplete ? 'Payment Already Completed' : 'Application Locked'}
                  </div>
                  <div style={{ fontSize: '0.8em', opacity: 0.85 }}>
                    {isPaymentComplete ? 'No further payment required.' : 'Cannot be modified.'}
                  </div>
                </div>
              </div>
            ) : hidePaymentButton ? (
              <div style={{
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                color: '#92400e',
                padding: '16px 24px',
                borderRadius: '12px',
                fontSize: '0.95em',
                fontWeight: 500,
                textAlign: 'center',
                border: '1px solid #f59e0b',
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.2)',
                maxWidth: '400px'
              }}>
                <div style={{ fontSize: '1.5em', marginBottom: '8px' }}>⏸️</div>
                <div style={{ fontWeight: 600, marginBottom: '4px' }}>Payment Temporarily Unavailable</div>
                <div style={{ fontSize: '0.85em', opacity: 0.9 }}>Please check back later or contact support.</div>
              </div>
            ) : (
              <>
                {missingFields.length > 0 && (
                  <div style={{
                    background: 'rgba(239, 68, 68, 0.1)',
                    color: '#dc2626',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    fontSize: '0.85em',
                    fontWeight: 500,
                    textAlign: 'center',
                    border: '1px solid rgba(239, 68, 68, 0.3)'
                  }}>
                    ⚠️ {missingFields.length} required field{missingFields.length > 1 ? 's' : ''} missing - Complete all fields to proceed
                  </div>
                )}
                <ProceedButton
                  onClick={handleSubmitApplication}
                  disabled={isSaving || !applicationId || !isOnline || missingFields.length > 0}
                  title={
                    missingFields.length > 0 ? `Complete ${missingFields.length} missing field(s) to proceed` : 
                    undefined
                  }
                  style={{
                    background: missingFields.length > 0 
                      ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)' 
                      : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    cursor: missingFields.length > 0 ? 'not-allowed' : 'pointer',
                    opacity: missingFields.length > 0 ? 0.7 : 1
                  }}
                >
                  {isSaving && !saveMessage.startsWith('Saving Draft') ? 'Submitting...' : 'PROCEED TO PAYMENT'}
                </ProceedButton>
              </>
            )}
          </>
	    )}
	  </ActionButtonsContainer>
      
      {/* Session Timer */}
      {!isViewMode && (
        <SessionTimer 
          sessionDuration={30}
          warningTime={5}
          onSessionExpired={() => navigate('/')}
          onSaveState={async () => {
            if (applicationId) {
              await saveToDatabaseCallback(formData, true, applicationId);
            }
          }}
        />
      )}

      {/* Home Icon Button - Above Save Icon */}
      <button
        onClick={() => {
          // Skip save modal if application is locked
          if (applicationId && isOnline && !isApplicationLocked) {
            setPendingNavigation('/user-dashboard');
            setShowSaveModal(true);
          } else {
            navigate('/user-dashboard');
          }
        }}
        title="Go to Dashboard"
        style={{
          position: 'fixed',
          left: '20px',
          bottom: '150px',
          zIndex: 1000,
          height: '44px',
          padding: '0 16px 0 12px',
          borderRadius: '22px',
          border: 'none',
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          color: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          fontSize: '14px',
          fontWeight: 600,
          boxShadow: '0 4px 14px rgba(99, 102, 241, 0.35)',
          transition: 'all 0.25s ease'
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
          <polyline points="9 22 9 12 15 12 15 22"/>
        </svg>
        Home
      </button>

      {/* Save Icon Button - Above Back Button - Hidden when application is locked */}
      {!isViewMode && !isApplicationLocked && (
        <button
          onClick={() => saveToDatabaseCallback(formData, true, applicationId)}
          disabled={isSaving || !applicationId || !isOnline}
          title="Save Draft"
          style={{
            position: 'fixed',
            left: '20px',
            bottom: '98px',
            zIndex: 1000,
            height: '44px',
            padding: '0 16px 0 12px',
            borderRadius: '22px',
            border: 'none',
            background: isSaving ? '#86efac' : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
            color: 'white',
            cursor: isSaving || !applicationId || !isOnline ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: 600,
            boxShadow: '0 4px 14px rgba(34, 197, 94, 0.35)',
            transition: 'all 0.25s ease',
            opacity: isSaving || !applicationId || !isOnline ? 0.6 : 1
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
            <polyline points="17 21 17 13 7 13 7 21"/>
            <polyline points="7 3 7 8 15 8"/>
          </svg>
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      )}
      
      {isViewMode && (
        <PayNowButton 
          onClick={handleDownloadPdf} 
          disabled={!isOnline}
          style={{ position: 'fixed', left: '95px', bottom: '31.5px', zIndex: 1000, margin: '0', height: '57px', borderRadius: '4px', width: '145px', fontSize: '12px', whiteSpace: 'nowrap', textAlign: 'center', fontWeight: 'bold', letterSpacing: '0.2px' }}
        > 
          DOWNLOAD PDF
        </PayNowButton>
      )}
      
      {isLoading && (
        <LoadingModalOverlay>
          <LoadingModalContent>
            <LoadingSpinner />
            <LoadingText>Please wait...</LoadingText>
            <LoadingSubText>Loading your application...</LoadingSubText>
          </LoadingModalContent>
        </LoadingModalOverlay>
      )}
      
      {isPdfGenerating && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
            textAlign: 'center',
            width: '300px'
          }}>
            <p style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#333' }}>Please wait... Generating PDF</p>
            <div style={{ width: '100%', backgroundColor: '#e0e0e0', borderRadius: '4px', height: '20px', overflow: 'hidden' }}>
              <div 
                style={{ 
                  width: `${compressionProgress}%`, 
                  backgroundColor: '#4CAF50', 
                  height: '100%',
                  transition: 'width 0.3s ease'
                }}
              />
            </div>
            <p style={{ margin: '10px 0 0 0', fontSize: '14px', color: '#666' }}>
              Compressing: {compressionProgress}%
            </p>
          </div>
        </div>
      )}

      {/* Save Progress Modal */}
      {showSaveModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2200,
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '28px 32px',
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            maxWidth: '380px',
            width: '90%',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '2.5em', marginBottom: '12px' }}>💾</div>
            <h3 style={{ margin: '0 0 10px 0', color: '#2c3e50', fontSize: '1.15em', fontWeight: 600 }}>
              Save your progress?
            </h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '0.9em', color: '#666', lineHeight: 1.5 }}>
              Would you like to save your form before continuing?
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={handleSkipSaveAndNavigate}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f1f1f1',
                  color: '#555',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.9em',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Skip
              </button>
              <button
                type="button"
                onClick={handleSaveAndNavigate}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#27ae60',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.9em',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Save & Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Brochure PDF Modal */}
      {showBrochureModal && (
        <div 
          onClick={() => setShowBrochureModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 2000
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: '90vw',
              maxWidth: '900px',
              height: '85vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#f8fafc'
            }}>
              <h3 style={{ margin: 0, color: '#1e40af', fontWeight: 600 }}>📄 MNGL DODO Brochure</h3>
              <button
                onClick={() => setShowBrochureModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '4px 8px',
                  borderRadius: '4px'
                }}
              >
                ✕
              </button>
            </div>
            <iframe
              src="/brochures/mngl-dodo-brochure.pdf"
              style={{
                flex: 1,
                width: '100%',
                border: 'none'
              }}
              title="MNGL DODO Brochure"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ApplicationFormPage14;